(() => {
  const isDesktop = () =>
    window.matchMedia("(min-width: 990px) and (hover:hover)").matches;

  let globalMouseLocs = [];

  const toArray = (list) => Array.from(list || []);

  const add = (el, cls) => el && el.classList.add(cls);
  const remove = (el, cls) => el && el.classList.remove(cls);

  const closestLiWithDropdown = (el) => {
    const li = el && el.closest && el.closest("li");
    if (!li) return null;
    const dropdown = li.querySelector(":scope > ul.dropdown");
    return dropdown ? li : null;
  };

  const point = (e) => ({ x: e.clientX, y: e.clientY });

  const slope = (a, b) => (b.y - a.y) / (b.x - a.x);

  const withinRect = (p, r) =>
    p.x >= r.left && p.x <= r.right && p.y >= r.top && p.y <= r.bottom;

  const distanceToRect = (p, r) => {
    const dx = Math.max(r.left - p.x, 0, p.x - r.right);
    const dy = Math.max(r.top - p.y, 0, p.y - r.bottom);
    return Math.hypot(dx, dy);
  };

  const normalizeAngle = (a) => {
    const twoPi = Math.PI * 2;
    const n = a % twoPi;
    return n < 0 ? n + twoPi : n;
  };

  const minimalCoveringArc = (angles) => {
    const sorted = [...angles].sort((a, b) => a - b);
    const twoPi = Math.PI * 2;
    const extended = [...sorted, sorted[0] + twoPi];
    let maxGap = -1;
    let maxGapIdx = 0;
    for (let i = 0; i < sorted.length; i += 1) {
      const gap = extended[i + 1] - extended[i];
      if (gap > maxGap) {
        maxGap = gap;
        maxGapIdx = i;
      }
    }
    const start = extended[maxGapIdx + 1] % twoPi;
    const end = extended[maxGapIdx] % twoPi;
    return { start, end };
  };

  const isAngleInArc = (angle, start, end) =>
    start <= end ? angle >= start && angle <= end : angle >= start || angle <= end;

  const isHeadingToRect = (prev, curr, rect) => {
    if (!prev || !curr) return false;
    if (withinRect(curr, rect)) return true;

    const prevDist = distanceToRect(prev, rect);
    const currDist = distanceToRect(curr, rect);
    if (currDist > prevDist) return false;

    const corners = [
      { x: rect.left, y: rect.top },
      { x: rect.left, y: rect.bottom },
      { x: rect.right, y: rect.top },
      { x: rect.right, y: rect.bottom },
    ];

    const arc = minimalCoveringArc(
      corners.map((c) => normalizeAngle(Math.atan2(c.y - prev.y, c.x - prev.x)))
    );
    const dir = normalizeAngle(Math.atan2(curr.y - prev.y, curr.x - prev.x));
    return isAngleInArc(dir, arc.start, arc.end);
  };

  const calcDelay = ({ dropdownEl, activeRow, mouseLocs, lastDelayLoc }) => {
    if (!activeRow) return 0;
    const submenu = activeRow.querySelector(":scope > ul.submenu");
    if (!submenu) return 0;

    const rect = submenu.getBoundingClientRect();
    const lastLoc = mouseLocs[mouseLocs.length - 1];
    const currLoc = mouseLocs[0];
    if (!lastLoc || !currLoc) return 0;

    if (withinRect(currLoc, rect)) return 0;

    if (lastDelayLoc && currLoc.x === lastDelayLoc.x && currLoc.y === lastDelayLoc.y)
      return 0;

    return isHeadingToRect(lastLoc, currLoc, rect) ? 300 : 0;
  };

  const initDropdownAim = (dropdownEl) => {
    let activeRow = null;
    let timeoutId = null;
    let lastDelayLoc = null;
    let mouseLocs = [];

    const rows = toArray(dropdownEl.querySelectorAll(":scope > li"));

    const setActiveRow = (row) => {
      rows.forEach((r) => remove(r, "menu-aim-open"));
      activeRow = row;
      if (row) add(row, "menu-aim-open");
    };

    const activate = (row) => {
      if (!row) return;
      if (row === activeRow) return;
      setActiveRow(row);
    };

    const possiblyActivate = (row) => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }

      const delay = calcDelay({
        dropdownEl,
        activeRow,
        mouseLocs,
        lastDelayLoc,
      });

      if (!delay) {
        lastDelayLoc = null;
        activate(row);
        return;
      }

      lastDelayLoc = mouseLocs[mouseLocs.length - 1] || lastDelayLoc;
      timeoutId = window.setTimeout(() => possiblyActivate(row), delay);
    };

    rows.forEach((row) => {
      row.addEventListener("mouseenter", () => possiblyActivate(row));
      row.addEventListener("focusin", () => activate(row));
    });

    dropdownEl.addEventListener("mouseleave", () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      lastDelayLoc = null;
      mouseLocs = [];
    });

    const syncMouseLocs = () => {
      mouseLocs = globalMouseLocs;
    };

    dropdownEl.addEventListener("mouseenter", syncMouseLocs);
    dropdownEl.addEventListener("mousemove", syncMouseLocs);
  };

  const initContainer = (containerEl) => {
    const rootList =
      containerEl.querySelector(".list-menu--inline") ||
      containerEl.querySelector(".product_service__list");
    if (!rootList) return;

    let closeTimer = null;

    const rootLis = toArray(rootList.querySelectorAll(":scope > li"));
    let activeTopLi = null;

    const clearAllOpen = () =>
      toArray(rootList.querySelectorAll("li.menu-aim-open")).forEach((li) =>
        remove(li, "menu-aim-open")
      );

    const closeAll = () => {
      activeTopLi = null;
      clearAllOpen();
    };

    const openLi = (li) => {
      if (!li) return;
      if (activeTopLi !== li) {
        clearAllOpen();
        activeTopLi = li;
      }
      rootLis.forEach((x) => (x === li ? add(x, "menu-aim-open") : remove(x, "menu-aim-open")));
    };

    rootList.addEventListener("mouseenter", () => {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
    });

    rootList.addEventListener("mouseleave", () => {
      if (closeTimer) window.clearTimeout(closeTimer);
      const attemptClose = () => {
        const top =
          activeTopLi || rootList.querySelector(":scope > li.menu-aim-open");
        const openDropdown = top && top.querySelector(":scope > ul.dropdown");
        const openRow = openDropdown && openDropdown.querySelector(":scope > li.menu-aim-open");
        const openSubmenu = openRow && openRow.querySelector(":scope > ul.submenu");
        const rect = openSubmenu && openSubmenu.getBoundingClientRect();
        const curr = globalMouseLocs[0];
        const prev = globalMouseLocs[1];

        if (rect && (!curr || !prev)) {
          closeTimer = window.setTimeout(attemptClose, 80);
          return;
        }

        if (rect && curr && prev && isHeadingToRect(prev, curr, rect)) {
          closeTimer = window.setTimeout(attemptClose, 150);
          return;
        }

        closeAll();
      };

      closeTimer = window.setTimeout(attemptClose, 250);
    });

    rootList.addEventListener("focusin", (e) => {
      const li = closestLiWithDropdown(e.target);
      if (li) openLi(li);
    });

    rootList.addEventListener("focusout", () => {
      if (closeTimer) window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => closeAll(), 200);
    });

    rootLis.forEach((li) => {
      const dropdown = li.querySelector(":scope > ul.dropdown");
      if (!dropdown) return;

      li.addEventListener("mouseenter", () => openLi(li));
      li.addEventListener("focusin", () => openLi(li));

      initDropdownAim(dropdown);
    });
  };

  const boot = () => {
    if (!isDesktop()) return;

    add(document.documentElement, "menu-aim-enabled");

    const recordMouse = (e) => {
      globalMouseLocs = [point(e), ...globalMouseLocs].slice(0, 3);
    };

    document.addEventListener("mousemove", recordMouse, { passive: true });
    document.addEventListener("mouseover", recordMouse, { passive: true });

    const containers = toArray(
      document.querySelectorAll(".header__inline-menu, .product_service__wrapper")
    );
    containers.forEach(initContainer);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();


